import { useEffect } from 'react';
import {
  articlesApi,
  ValidateItemCodeResponse,
} from '@texas/api/endpoints/articlesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { HttpClientResponse } from '@texas/api/httpClient/types';
import { buildArticleNumbers } from './articleNumbers';

const searchLimit = 1000;

interface RangeSearch {
  itemCode: string;
  start: number;
  end: number;
  padding: number;
  position: 'before' | 'after';
  minimumLength: number;
  maximumLength: number;
  knownAvailability?: Partial<Record<string, ValidateItemCodeResponse>> | null;
}

interface AvailableArticleRange {
  start: number;
  end: number;
  firstItemCode: string;
  lastItemCode: string;
}

export function useAvailableArticleRange({
  enabled,
  ...params
}: RangeSearch & { enabled: boolean }) {
  const resource = useApiResource(findAvailableArticleRange);
  const { refetch, set } = resource;
  const {
    itemCode,
    start,
    end,
    padding,
    position,
    minimumLength,
    maximumLength,
    knownAvailability,
  } = params;
  useEffect(() => {
    set(null);
    if (!enabled) return;
    const controller = new AbortController();
    void refetch(
      {
        itemCode,
        start,
        end,
        padding,
        position,
        minimumLength,
        maximumLength,
        knownAvailability,
      },
      controller.signal,
    );
    return () => {
      controller.abort();
      set(null);
    };
  }, [
    enabled,
    itemCode,
    start,
    end,
    padding,
    position,
    minimumLength,
    maximumLength,
    knownAvailability,
    refetch,
    set,
  ]);
  return resource;
}

export async function findAvailableArticleRange(
  params: RangeSearch,
  signal: AbortSignal,
  validate = articlesApi.validateItemCodes,
): Promise<HttpClientResponse<AvailableArticleRange | null>> {
  const {
    itemCode,
    start,
    end,
    padding,
    position,
    minimumLength,
    maximumLength,
    knownAvailability,
  } = params;
  const count = end - start + 1;
  const isCancelled = () => signal.aborted;
  const empty: HttpClientResponse<AvailableArticleRange | null> = {
    hasError: false,
    status: 200,
    headers: {},
    data: null,
  };
  if (
    !buildArticleNumbers('range', itemCode, '', start, end, padding, position)
      .length
  )
    return empty;
  let consecutive = 0;
  let firstItemCode = '';
  for (let offset = 1; offset <= searchLimit && !isCancelled(); ) {
    const batchStart = start + offset;
    const batchEnd = Math.min(
      start + searchLimit,
      batchStart + count - consecutive - 1,
    );
    const candidates = buildArticleNumbers(
      'range',
      itemCode,
      '',
      batchStart,
      batchEnd,
      padding,
      position,
    );
    if (!candidates.length) return empty;
    // Number widths only grow, so no later range can fit once the maximum is exceeded.
    const fitting = candidates.filter(
      (candidate) => candidate.itemCode.length <= maximumLength,
    );
    if (!fitting.length) return empty;
    const uncheckedCodes = fitting
      .filter((candidate) => !knownAvailability?.[candidate.itemCode])
      .map((candidate) => candidate.itemCode);
    const response: HttpClientResponse<
      Partial<Record<string, ValidateItemCodeResponse>>
    > = uncheckedCodes.length
      ? await validate(uncheckedCodes, { signal })
      : { hasError: false, status: 200, headers: {}, data: {} };

    if (response.hasError) return response;
    if (isCancelled()) return empty;
    for (const [index, candidate] of fitting.entries()) {
      const result =
        knownAvailability?.[candidate.itemCode] ??
        response.data[candidate.itemCode];
      if (result?.errorType === 'ErpError' && result.errorMessage) {
        return {
          hasError: true,
          error: { status: 502, message: result.errorMessage, errors: {} },
        };
      }
      if (candidate.itemCode.length >= minimumLength && result?.isValid) {
        if (!consecutive) firstItemCode = candidate.itemCode;
        consecutive++;
        if (consecutive === count) {
          const rangeEnd = batchStart + index;
          return {
            ...response,
            data: {
              start: rangeEnd - count + 1,
              end: rangeEnd,
              firstItemCode,
              lastItemCode: candidate.itemCode,
            },
          };
        }
      } else {
        consecutive = 0;
      }
    }
    if (fitting.length < candidates.length) return empty;
    offset += candidates.length;
  }
  return empty;
}
